import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Grid } from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { getWarehouseSelectedLocation } from '../../navigation/components/WarehouseLocationToggle';
import LabelTemplateAccordion from '../components/LabelTemplateAccordion';
import { useSaveWarehouseLabelTemplate, useWarehouseLabelConfigs } from '../hooks/useWarehouseLabelConfig';
import {
  LabelTemplateSlug,
  TemplateDraft,
  WarehouseLabelConfigPayload,
  labelConfigUrlFor,
  warehouseLabelConfigsUrl,
  withLocation,
} from '../model';

const sameCodes = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const draftsFromPayload = (payload: WarehouseLabelConfigPayload): Record<string, TemplateDraft> =>
  Object.fromEntries(
    payload.templates.map((template) => [
      template.slug,
      { body: template.body, deptCodes: template.departments.map((d) => d.code) },
    ]),
  );

const WarehouseLabelConfigPage: React.FC = () => {
  const [location] = useState(() => getWarehouseSelectedLocation());
  const [drafts, setDrafts] = useState<Record<string, TemplateDraft>>({});
  const [expanded, setExpanded] = useState<LabelTemplateSlug | null>(null);

  // Body edits latch on a user keystroke rather than a string comparison: the rich editor
  // rewrites the stored HTML into its own canonical form on load, which no comparison can
  // tell from a real edit. Department edits are compared against the server instead, so a
  // cross-template move clears itself once the template that claimed it saves.
  const [bodyDirty, setBodyDirty] = useState<Set<string>>(new Set());
  const [deptsTouched, setDeptsTouched] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const configsQuery = useWarehouseLabelConfigs(location);
  const saveTemplate = useSaveWarehouseLabelTemplate();
  const payload = configsQuery.data?.data;
  const queryKey = [warehouseLabelConfigsUrl, { location }];

  // Read through a ref so the reseed below depends only on the payload. Depending on the
  // dirty sets directly would re-run it on every keystroke and clobber what is being typed.
  const pendingRef = useRef({ bodyDirty, deptsTouched });
  pendingRef.current = { bodyDirty, deptsTouched };

  // Seed the drafts when the payload lands or a save returns a new one, keeping whatever
  // the admin has touched in another accordion.
  useEffect(() => {
    if (!payload) return;
    const pending = pendingRef.current;
    setDrafts((current) => {
      const seeded = draftsFromPayload(payload);
      Object.keys(seeded).forEach((slug) => {
        if (!current[slug]) return;
        if (pending.bodyDirty.has(slug)) seeded[slug] = { ...seeded[slug], body: current[slug].body };
        // Keep local membership when it already diverged from the GET (loser of an unsaved
        // move is not in deptsTouched). A later winner save still reseeds the loser because
        // that payload matches the reduced draft.
        if (pending.deptsTouched.has(slug) || !sameCodes(current[slug].deptCodes, seeded[slug].deptCodes)) {
          seeded[slug] = { ...seeded[slug], deptCodes: current[slug].deptCodes };
        }
      });
      return seeded;
    });
  }, [payload]);

  const serverBySlug = useMemo(
    () => Object.fromEntries((payload?.templates ?? []).map((template) => [template.slug, template])),
    [payload],
  );

  const templateNames = useMemo(
    () => Object.fromEntries((payload?.templates ?? []).map((template) => [template.slug, template.name])),
    [payload],
  );

  // Exclusivity, mirrored from the drafts rather than the server, so the "currently in X"
  // hint follows an unsaved move instead of the last save.
  const codeOwner = useMemo(() => {
    const owner: Record<string, LabelTemplateSlug> = {};
    Object.entries(drafts).forEach(([slug, draft]) => {
      draft.deptCodes.forEach((code) => {
        owner[code] = slug as LabelTemplateSlug;
      });
    });
    return owner;
  }, [drafts]);

  const isDirty = (slug: string): boolean => {
    const draft = drafts[slug];
    const server = serverBySlug[slug];
    if (!draft || !server) return false;
    return (
      bodyDirty.has(slug) ||
      !sameCodes(
        draft.deptCodes,
        server.departments.map((d) => d.code),
      )
    );
  };

  const dirtyNames = (payload?.templates ?? []).filter((t) => isDirty(t.slug)).map((t) => t.name);

  const totalDepartments = payload?.departments.length ?? 0;
  const unassignedCount = (payload?.departments ?? []).filter((dept) => !codeOwner[dept.code]).length;

  // Leaving the page by any other route — sidebar link, Back, reload, tab close — also
  // loses edits, and the Prompt below does not cover those.
  useEffect(() => {
    if (dirtyNames.length === 0) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyNames.length]);

  const handleBodyChange = (slug: LabelTemplateSlug, value: string, source: string) => {
    // Quill fires onChange on mount, which can beat the payload reseed. Spreading a missing
    // current[slug] would drop deptCodes and crash the accordion on .length.
    setDrafts((current) => {
      const existing = current[slug] ?? { body: '', deptCodes: [] };
      return { ...current, [slug]: { ...existing, body: value } };
    });
    if (source === 'user') setBodyDirty((current) => new Set(current).add(slug));
  };

  // Picking a department here takes it out of whatever template held it, so no department
  // is ever shown in two lists. The losing template is not marked touched: saving this one
  // moves the department server-side, and the response reseeds the loser's draft.
  const handleDeptCodesChange = (slug: LabelTemplateSlug, codes: string[]) => {
    setDrafts((current) => {
      const existing = current[slug] ?? { body: '', deptCodes: [] };
      const next = { ...current, [slug]: { ...existing, deptCodes: codes } };
      const claimed = new Set(codes);
      Object.keys(next).forEach((other) => {
        if (other === slug) return;
        const kept = next[other].deptCodes.filter((code) => !claimed.has(code));
        if (kept.length !== next[other].deptCodes.length) next[other] = { ...next[other], deptCodes: kept };
      });
      return next;
    });
    setDeptsTouched((current) => new Set(current).add(slug));
  };

  const withoutSlug = (set: Set<string>, slug: string): Set<string> => {
    const next = new Set(set);
    next.delete(slug);
    return next;
  };

  const handleSave = async (slug: LabelTemplateSlug) => {
    const draft = drafts[slug];
    try {
      const response = await saveTemplate.mutateAsync({
        url: withLocation(labelConfigUrlFor(slug), location),
        data: { config: { body: draft.body, dept_codes: draft.deptCodes } },
      });
      setBodyDirty((current) => withoutSlug(current, slug));
      setDeptsTouched((current) => withoutSlug(current, slug));
      // The response is the whole payload, so the template that lost a department refreshes
      // in the same beat and no refetch is needed.
      queryClient.setQueryData(queryKey, response);
      showSuccessSnackBar?.('Template saved.');
    } catch {
      showErrorSnackBar?.('Failed to save template.');
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <PageHeader mainTitle="Warehouse — Label Configuration" titleIcon={<LabelIcon />} />
      </Grid>

      {configsQuery.isLoading && (
        <Grid item xs={12}>
          <LoadingWrapper isLoading message="Loading..." />
        </Grid>
      )}

      {configsQuery.isError && (
        <Grid item xs={12}>
          <ErrorMessage message="Failed to load label configurations." />
        </Grid>
      )}

      {payload && (
        <Grid item xs={12}>
          <Alert severity={unassignedCount > 0 ? 'warning' : 'info'}>
            {totalDepartments} {totalDepartments === 1 ? 'department' : 'departments'} in total.{' '}
            {unassignedCount} {unassignedCount === 1 ? 'is' : 'are'} not assigned to any label group.
          </Alert>
        </Grid>
      )}

      {payload?.templates.map((template) => (
        <Grid item xs={12} key={template.slug}>
          <LabelTemplateAccordion
            template={template}
            draft={drafts[template.slug] ?? { body: template.body, deptCodes: [] }}
            departments={payload.departments}
            codeOwner={codeOwner}
            templateNames={templateNames}
            markers={payload.template_variables}
            expanded={expanded === template.slug}
            isDirty={isDirty(template.slug)}
            isSaving={saveTemplate.isPending}
            onToggle={() => setExpanded(expanded === template.slug ? null : template.slug)}
            onBodyChange={(value, source) => handleBodyChange(template.slug, value, source)}
            onDeptCodesChange={(codes) => handleDeptCodesChange(template.slug, codes)}
            onSave={() => handleSave(template.slug)}
          />
        </Grid>
      ))}

      <Prompt
        when={dirtyNames.length > 0}
        message={`You have unsaved edits to ${dirtyNames.join(', ')}. Leave without saving?`}
      />
    </Grid>
  );
};

export default WarehouseLabelConfigPage;
